import React, { useCallback, useEffect, useMemo, useState } from "react";
import { COLORS } from "rylak-common/src/constants";
import UserAvatar from "~/components/userAvatar/UserAvatar.jsx";
import OpenCloseChevronIcon from "~/components/icons/OpenCloseChevronIcon.jsx";
import styles from "./ReturnLogEvents.module.scss";
import classnames from "classnames";
import ObjectAsJson from "~/components/ObjectAsJson.jsx";
import { TurnrButton } from "~/components/buttons/TurnrButton";
import { TurnrCard } from "~/components/card/TurnrCard.jsx";
import { getEntityEventComment, getEntityEventEmail, getEntityEventEntity, getEntityEventFrontendData, getEntityEventSMS, isEventTypeCreate } from "~/cores/entityEventCore";
import { CHIP_COLOR_TYPES, CustomChip } from "~/components/chips/CustomChip.jsx";
import SkeletonLoaderCardSlim from "~/components/loading/skeletons/SkeletonLoaderCardSlim.jsx";
import CustomerLink from "~/components/links/CustomerLink.jsx";
import { getUserDisplayName } from "~/cores/userCore";
import WmsLogoIcon from "~/components/icons/WmsLogoIcon.jsx";
import { humanReadableDate, timeFromDate, toDate } from "rylak-common";
import { HumanReadableDateHover } from "~/components/HumanReadableDateTimeHover.jsx";
import TurnrModal, { TurnrModalContainer, TurnrModalContent, TurnrModalFooter } from "~/components/modals/TurnrModal.jsx";
import CustomerNotificationContent from "./CustomerNotificationContent";
import { WarehouseReportInfoModalContent } from "../../warehouse-reports/(all)/WarehouseReportInfoModalContent";
import PriceSetupCard from "../../settings/price-setups/_pricesFeatures/PriceSetupCard";
import { getCurrentDateClient } from "~/cores/appCore.js";

const SHOW_MORE_LIMIT = 5;

const TIME_WIDTH_SMALL = 40;
const TIME_WIDTH_LARGE = 75;
const TIME_MARGIN_RIGHT = 10;
const CONTAINER_MARGIN_LEFT = 10;
const ICON_SIZE = 28;
const LINE_WIDTH = 2;
const RIGHT_PART_MARGIN_LEFT = 14;

function ClickableChip({ label, modalTitle, modalContent }) {
    const [modalOpen, setModalOpen] = useState(false);

    return (
        <div
            style={{
                width: "fit-content",
            }}
        >
            <TurnrModal open={modalOpen} onClose={setModalOpen} title={modalTitle}>
                {modalContent}
                <TurnrModalFooter onCancel={setModalOpen} cancelText={"Close"} showSubmitButton={false} />
            </TurnrModal>
            <CustomChip label={label} colorType={CHIP_COLOR_TYPES.WHITE} disableTooltip={true} onClick={() => setModalOpen(true)} size={"small"} />
        </div>
    );
}

function ClickableChips({ appProps, entityEvent }) {
    const chips = useMemo(() => {
        const email = getEntityEventEmail(entityEvent);
        const sms = getEntityEventSMS(entityEvent);
        const isWarehouseReturnEvent = entityEvent?.eventEntityType === "WAREHOUSE_RETURN";

        // Just so that old wrong price list data objects to not show we check that an ID is present
        const priceList = entityEvent?.data?.priceList?.id && entityEvent?.data?.priceList;

        const array = [];
        if (email) {
            array.push(<ClickableChip label={"View email"} modalContent={<CustomerNotificationContent customerNotification={email} />} />);
        }
        if (sms) {
            array.push(<ClickableChip label={"View sms"} modalContent={<CustomerNotificationContent customerNotification={sms} />} />);
        }
        if (isWarehouseReturnEvent) {
            array.push(
                <ClickableChip
                    label={"View report"}
                    modalContent={
                        <WarehouseReportInfoModalContent
                            appProps={appProps}
                            warehouseReport={getEntityEventEntity(entityEvent)}
                            warehouseReportSummary={getEntityEventFrontendData(entityEvent)?.content?.entitySummary}
                        />
                    }
                />,
            );
        }
        if (priceList) {
            array.push(
                <ClickableChip
                    modalTitle={"Price Setup"}
                    label={"View Price Setup"}
                    modalContent={
                        <TurnrModalContainer>
                            <TurnrModalContent>
                                <PriceSetupCard appProps={appProps} priceSetup={priceList} readOnly={true} />
                            </TurnrModalContent>
                            <TurnrModalContent>
                                <ObjectAsJson src={priceList} name={"As Json"} level={0} />
                            </TurnrModalContent>
                        </TurnrModalContainer>
                    }
                />,
            );
        }
        return array;
    }, [entityEvent]);

    if (chips.length === 0) {
        return null;
    }

    return <div style={{ marginTop: "5px" }}>{chips.map((chip) => chip)}</div>;
}

export default function ReturnLogEvents(props) {
    // TODO this whole thing should be refactored together with backend
    const { returnEntityEventsService, returns, loadingCreateComment, appProps } = props;

    if (!returnEntityEventsService?.data?.length) {
        return null;
    }

    const [expandedEvents, setExpandedEvents] = useState([]);
    const [showAllEvents, setShowAllEvents] = useState(returns?.length < 2 || returnEntityEventsService?.data?.length <= SHOW_MORE_LIMIT);

    useEffect(() => {
        if (returns.length === 1) {
            setShowAllEvents(true);
        }
        if (returnEntityEventsService?.data?.length <= SHOW_MORE_LIMIT) {
            setShowAllEvents(true);
        }
    }, [returns, returnEntityEventsService]);

    const onEventClick = useCallback(
        (id) => {
            if (expandedEvents.includes(id)) {
                const index = expandedEvents.indexOf(id);
                expandedEvents.splice(index, 1);
                setExpandedEvents([...expandedEvents]);
            } else {
                expandedEvents.push(id);
                setExpandedEvents([...expandedEvents]);
            }
        },
        [expandedEvents],
    );

    const showAllEventsButton = !showAllEvents && returns?.length > 1 && returnEntityEventsService?.data?.length > SHOW_MORE_LIMIT;

    let latestDate = null;
    let TIME_WIDTH = TIME_WIDTH_SMALL;

    return (
        <div
            className={returns?.length === 2 && styles["two-returns"]}
            style={{
                display: "flex",
                flexDirection: "column",
                position: "relative",
                marginLeft: "10px",
                // marginRight: "10px",
                // marginBottom: "15px",
            }}
        >
            <div
                style={{
                    marginTop: "30px",
                    marginLeft: CONTAINER_MARGIN_LEFT,
                }}
            >
                {loadingCreateComment && (
                    <div
                        style={{
                            position: "relative",
                            marginBottom: "13px",
                            height: ICON_SIZE,
                            width: "100%",
                            // background: COLORS.BACKGROUND,
                            zIndex: 1,
                        }}
                    >
                        <SkeletonLoaderCardSlim />
                    </div>
                )}
                {returnEntityEventsService?.data?.map((entityEvent, index) => {
                    // const l = data?.frontendData?.label?.toLowerCase();
                    // const icon = l?.includes("edit")
                    //     ? faPen
                    //     : l?.includes("create") || l?.includes("added")
                    //     ? faPlus
                    //     : l?.includes("remove")
                    //     ? faTrash
                    //     : faCircle;

                    const open = expandedEvents?.includes(entityEvent?.id);
                    const comment = getEntityEventComment(entityEvent);
                    const user = entityEvent?.frontendData?.user || entityEvent?.user;
                    let labelComponent = comment || entityEvent?.frontendData?.label;

                    const isWarehouseReturnEvent = entityEvent?.eventEntityType === "WAREHOUSE_RETURN";
                    const clickable = !!entityEvent?.actionInputData && !comment && !isWarehouseReturnEvent;

                    let date = null;

                    const dateTime = entityEvent.startedAt;
                    let newDate = humanReadableDate({ dateTime });

                    if (latestDate !== newDate) {
                        date = humanReadableDate({ dateTime });
                        latestDate = date;
                    }

                    let time = timeFromDate(entityEvent.startedAt);
                    let currentDate = toDate(entityEvent.startedAt);
                    let diff = getCurrentDateClient(appProps) - currentDate;
                    let seconds = Math.floor(diff / 1000);

                    if (seconds <= 60) {
                        time = "Just now";
                    } else if (seconds <= 60 * 60) {
                        TIME_WIDTH = TIME_WIDTH_LARGE;
                        const minutes = Math.floor(seconds / 60);
                        time = minutes + " " + (minutes === 1 ? "minute" : "minutes") + " ago";
                    }

                    if (isEventTypeCreate(entityEvent)) {
                        if (entityEvent?.customer) {
                            labelComponent = (
                                <div>
                                    Return created by customer <CustomerLink customer={entityEvent?.customer} />
                                </div>
                            );
                        } else if (user) {
                            labelComponent = (
                                <div>
                                    Return created by Backoffice user{" "}
                                    <b>
                                        {getUserDisplayName({
                                            user,
                                        })}
                                    </b>
                                </div>
                            );
                        } else if (entityEvent?.wmsType) {
                            labelComponent = (
                                <div
                                    style={{
                                        display: "flex",
                                        // alignItems: "center",
                                        gap: "10px",
                                    }}
                                >
                                    Return created through webhook by
                                    <div
                                        style={
                                            {
                                                // marginTop: "-3px",
                                            }
                                        }
                                    >
                                        <WmsLogoIcon type={entityEvent?.wmsType} />
                                    </div>
                                </div>
                            );
                        }
                    }

                    return (
                        <div
                            style={{ zIndex: 1, position: "relative" }}
                            key={entityEvent?.id}
                            className={classnames(styles["event-container"], !showAllEvents && index >= SHOW_MORE_LIMIT && styles["event-container-hidden"])}
                        >
                            {date && (
                                <div
                                    key={entityEvent?.id}
                                    className={classnames(styles["event"])}
                                    style={{
                                        marginBottom: "10px",
                                        marginTop: index !== 0 && "30px",
                                    }}
                                >
                                    {/*<div*/}
                                    {/*    style={{*/}
                                    {/*        marginTop: "8px",*/}
                                    {/*        marginRight: TIME_MARGIN_RIGHT,*/}
                                    {/*        fontSize: "10px",*/}
                                    {/*        // width: TIME_WIDTH,*/}
                                    {/*    }}*/}
                                    {/*></div>*/}
                                    <div
                                        style={{
                                            width: ICON_SIZE,
                                            display: "flex",
                                            justifyContent: "center",
                                            // alignItems: "center",

                                            zIndex: 0,
                                        }}
                                    ></div>
                                    <div>
                                        <div
                                            className={classnames(styles["event"])}
                                            style={{
                                                display: "inline-flex",
                                                marginTop: "9px",
                                                // marginLeft: "11px",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: "11px",
                                                    marginTop: "-1px",
                                                    flex: "1",
                                                    marginLeft: RIGHT_PART_MARGIN_LEFT,
                                                    color: COLORS.SLIGHTLY_WEAK_TEXT,
                                                }}
                                            >
                                                <HumanReadableDateHover dateTime={dateTime} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div
                                className={classnames(styles["event"])}
                                style={{
                                    marginBottom: index !== returnEntityEventsService?.data.length - 1 && "13px",
                                }}
                            >
                                <div
                                    style={{
                                        background: COLORS.BACKGROUND,
                                        padding: "4px",
                                        width: ICON_SIZE,
                                        marginTop: !user && "6px",
                                        display: "flex",
                                        justifyContent: "center",
                                        // alignItems: "center",
                                        height: index !== returnEntityEventsService?.data.length - 1 && "fit-content",
                                        zIndex: 0,
                                    }}
                                >
                                    {user && <UserAvatar user={user} size={"small"} />}

                                    {!user && (
                                        <div
                                            style={{
                                                position: "relative",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                        >
                                            {/*<div style={{ visibility: "hidden" }}>*/}
                                            {/*    <UserAvatar*/}
                                            {/*        user={data?.user}*/}
                                            {/*        size={"small"}*/}
                                            {/*    />*/}
                                            {/*</div>*/}
                                            <div
                                                style={{
                                                    // position: "absolute",
                                                    background: COLORS.BROWN,
                                                    height: "8px",
                                                    width: "8px",
                                                    marginLeft: "1px",
                                                    borderRadius: "10px",
                                                }}
                                            ></div>
                                        </div>
                                    )}
                                    {/*<div*/}
                                    {/*    style={{*/}
                                    {/*        background: COLORS.GRAY,*/}
                                    {/*        width: "20px",*/}
                                    {/*        height: "20px",*/}
                                    {/*        borderRadius: "20px",*/}
                                    {/*    }}*/}
                                    {/*></div>*/}
                                </div>
                                <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <div>
                                        <div
                                            onClick={clickable ? () => onEventClick(entityEvent?.id) : null}
                                            className={classnames(styles["event"], clickable && styles["event-clickable"], open && styles["event-open"])}
                                            style={{
                                                display: "inline-flex",
                                                marginTop: comment ? "4px" : "9px",
                                                // marginLeft: "11px",
                                            }}
                                        >
                                            {/*<TurnrFontAwesomeIcon*/}
                                            {/*    icon={icon}*/}
                                            {/*    width={"10px"}*/}
                                            {/*    color={COLORS.WEAK_TEXT}*/}
                                            {/*/>*/}
                                            <div
                                                style={{
                                                    fontSize: "11px",
                                                    marginTop: "-1px",
                                                    flex: "1",
                                                    marginLeft: RIGHT_PART_MARGIN_LEFT,
                                                    background: comment && "white",
                                                    padding: comment && "5px 10px",
                                                    boxShadow: comment && "0 0 0 1px rgba(63, 63, 68, 0.05), 0 1px 2px 0 rgba(63, 63, 68, 0.15)",
                                                }}
                                            >
                                                {labelComponent}

                                                <ClickableChips appProps={appProps} entityEvent={entityEvent} />
                                            </div>
                                            {clickable ? (
                                                <div
                                                    className={styles["chevron"]}
                                                    style={{
                                                        marginLeft: "10px",
                                                        marginTop: "-3px",
                                                    }}
                                                >
                                                    <OpenCloseChevronIcon width={"5px"} open={open} />
                                                </div>
                                            ) : null}
                                        </div>
                                        {open && clickable && (
                                            <div
                                                style={{
                                                    fontSize: "12px",
                                                    marginLeft: "35px",
                                                    marginTop: "10px",
                                                }}
                                            >
                                                <ObjectAsJson name="data" backgroundColor={COLORS.BACKGROUND} level={1} src={JSON.parse(entityEvent?.actionInputData)} />
                                            </div>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            marginTop: "8px",
                                            marginRight: TIME_MARGIN_RIGHT,
                                            fontSize: "10px",
                                            color: COLORS.SLIGHTLY_WEAK_TEXT,
                                            width: TIME_WIDTH,
                                            whiteSpace: "nowrap",
                                            textAlign: "right",
                                        }}
                                    >
                                        {time}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div
                    style={{
                        background: COLORS.GRAY,
                        width: LINE_WIDTH,
                        height: "100%",
                        top: 0,
                        zIndex: 0,
                        left:
                            CONTAINER_MARGIN_LEFT +
                            // TIME_WIDTH +
                            // TIME_MARGIN_RIGHT +
                            ICON_SIZE / 2 -
                            LINE_WIDTH / 2,
                        position: "absolute",
                    }}
                />
            </div>
            {showAllEventsButton && (
                <TurnrCard
                    className={styles["show-more-button"]}
                    style={{
                        backgroundColor: COLORS.BACKGROUND,
                        border: "none",
                        boxShadow: "none",
                        outline: "none",
                    }}
                >
                    <div
                        style={{
                            width: "175px",
                            // marginLeft: "30px",
                            display: "flex",
                            // justifyContent: "center",
                        }}
                    >
                        <TurnrButton
                            onClick={() => setShowAllEvents(true)}
                            color={COLORS.SLIGHTLY_WEAK_TEXT}
                            size={"small"}
                            fullWidth
                            variant={"text"}
                            style={
                                {
                                    // background: "white",
                                }
                            }
                        >
                            <span style={{ fontSize: "11px" }}>Show more</span>
                        </TurnrButton>
                    </div>
                </TurnrCard>
            )}
        </div>
    );
}
